/**
 * RegistrationQRPage — the STAFF screen (any staff/coach/admin) that generates an
 * in-person registration QR on their phone. They pick the kind (member vs
 * non-member), the backend mints a short-lived signed token, and we render the
 * /join?token=... URL as a QR for the guest to scan. The code expires; staff can
 * regenerate to rotate. The kind is baked into the token server-side, so a guest
 * can never switch a guest sign-up into a member sign-up.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Box, Container, Paper, Typography, ToggleButton, ToggleButtonGroup, Button, Stack,
  Alert, CircularProgress, Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAppSelector } from '../hooks/useAppSelector';
import { useSnackbar } from '../components/common/SnackbarProvider';
import registrationService, { MintedToken, RegistrationKind } from '../api/services/registrationService';
import { getErrorMessage } from '../api/axiosConfig';

const RegistrationQRPage: React.FC = () => {
  const { user } = useAppSelector((s) => s.auth);
  const snackbar = useSnackbar();
  const isStaff = user?.role === 'staff' || user?.role === 'admin' || user?.role === 'superuser';

  const [kind, setKind] = useState<RegistrationKind>('non_member');
  const [minted, setMinted] = useState<MintedToken | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number>(0); // seconds to expiry

  const generate = useCallback(async (k: RegistrationKind) => {
    setLoading(true);
    setError(null);
    try {
      setMinted(await registrationService.mintToken(k));
    } catch (err) {
      setError(getErrorMessage(err));
      setMinted(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Live countdown to the token's expiry.
  useEffect(() => {
    if (!minted) return;
    const exp = new Date(minted.expires_at).getTime();
    const tick = () => setRemaining(Math.max(0, Math.round((exp - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [minted]);

  if (!isStaff) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 6 }}><Alert severity="error">This page is for staff only.</Alert></Box>
      </Container>
    );
  }

  const joinUrl = minted ? `${window.location.origin}/join?token=${minted.token}` : '';
  const expired = !!minted && remaining <= 0;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;

  const handleKind = (_: React.MouseEvent<HTMLElement>, k: RegistrationKind | null) => {
    if (!k) return;
    setKind(k);
    setMinted(null); // force a fresh generate for the newly-selected kind
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      snackbar.success('Join link copied.');
    } catch {
      snackbar.error('Could not copy the link.');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: { xs: 3, sm: 5 }, mb: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
          <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
            In-person registration QR
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick the sign-up type, show the QR, and have the person scan it on their phone to register
            their whole household on the spot. The code expires automatically — regenerate as needed.
          </Typography>

          <ToggleButtonGroup exclusive value={kind} onChange={handleKind} sx={{ mb: 2 }}>
            <ToggleButton value="member" color="success">Member</ToggleButton>
            <ToggleButton value="non_member" color="warning">Non-member / guest</ToggleButton>
          </ToggleButtonGroup>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!minted && !loading && (
            <Box>
              <Button variant="contained" size="large" onClick={() => generate(kind)}>Generate QR</Button>
            </Box>
          )}

          {loading && <Box sx={{ py: 6 }}><CircularProgress /></Box>}

          {minted && (
            <Stack spacing={2} alignItems="center">
              <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, opacity: expired ? 0.3 : 1 }}>
                <QRCodeCanvas value={joinUrl} size={240} level="M" marginSize={2} bgColor="#ffffff" fgColor="#000000" />
              </Box>
              {expired ? (
                <Chip color="error" label="Expired — regenerate" />
              ) : (
                <Chip color="success" variant="outlined"
                  label={`Expires in ${mins}:${String(secs).padStart(2, '0')}`} />
              )}
              <Stack direction="row" spacing={1}>
                <Button startIcon={<RefreshIcon />} variant="contained" onClick={() => generate(kind)}>
                  {expired ? 'Generate new code' : 'Regenerate'}
                </Button>
                <Button startIcon={<ContentCopyIcon />} variant="outlined" onClick={copyLink} disabled={expired}>
                  Copy link
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Registering as: {kind === 'member' ? 'Member' : 'Non-member / guest'}
              </Typography>
            </Stack>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default RegistrationQRPage;
